import type { RichDocument, RichNode } from '@cyphex/types';
import { parseInline } from './inline';
import { FENCE, listLine, standaloneBlock } from './blockRules';
import type { MentionTarget } from './mentions';

/**
 * Markdown → `RichDocument`. Deliberately a small, predictable subset:
 * fenced code, headings, bullet/ordered lists, blockquotes, rules, paragraphs.
 * The server stores the same shape, so what you type is what everyone sees.
 */
export function markdownToRich(
  source: string,
  /** Who an `@Name` may mean - see `parseInline`. */
  targets: readonly MentionTarget[] = [],
): RichDocument {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const content: RichNode[] = [];
  let paragraph: string[] = [];

  const flush = (): void => {
    if (paragraph.length === 0) return;
    content.push({ type: 'paragraph', content: parseInline(paragraph.join('\n'), 0, targets) });
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i] ?? '';
    const fence = FENCE.exec(line);

    if (fence) {
      flush();
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i] ?? '')) {
        body.push(lines[i] ?? '');
        i += 1;
      }
      content.push({
        type: 'code_block',
        attrs: { language: fence[1] || undefined },
        content: [{ type: 'text', text: body.join('\n') }],
      });
      continue;
    }

    if (line.trim() === '') {
      flush();
      continue;
    }

    const standalone = standaloneBlock(line, targets);
    if (standalone) {
      flush();
      content.push(standalone);
      continue;
    }

    const list = listLine(line, targets);
    if (list) {
      flush();
      const last = content[content.length - 1];
      if (last && last.type === list.kind) last.content = [...(last.content ?? []), list.item];
      else content.push({ type: list.kind, content: [list.item] });
      continue;
    }

    paragraph.push(line);
  }

  flush();
  return { type: 'doc', content };
}
